import {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
} from "react";
import { Button } from "@/components/ui/button-new";
import { ZoomIn, ZoomOut, RefreshCcw, RotateCwSquare } from "lucide-react";

/**
 * ImageViewer Component
 *
 * A responsive image viewer with pan, zoom, rotate, and flip capabilities.
 * This component provides a complete solution for viewing and manipulating images
 * with intuitive controls and smooth interactions.
 */

export interface ImageViewerHandle {
    /**
     * Resets the image to its original position, zoom level, and orientation
     */
    reset: () => void;
}

interface ImageViewerProps {
    /** URL of the image to display */
    image: string;
    /** Alternative text for the image */
    alt?: string;
    /** External trigger to reset the image state */
    resetImageState?: boolean;
    /** Whether to fit the image to container on initial render */
    fitToContainer?: boolean;
}

/**
 * An interactive image viewer component with pan, zoom, rotate and flip capabilities.
 *
 * This component is designed for viewing document-like images with:
 * - Horizontal panning via mouse drag or touch
 * - Vertical scrolling for tall images
 * - Button controls for zoom, rotation, and flipping
 * - Reset functionality to return to original state
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ImageViewer image="/path/to/receipt.jpg" alt="Receipt" />
 *
 * // With ref for programmatic control
 * const viewerRef = useRef<ImageViewerHandle>(null);
 * <ImageViewer ref={viewerRef} image="/path/to/receipt.jpg" />
 * // Later: viewerRef.current?.reset();
 * ```
 */

const disableGestures = true;

const ImageViewer = forwardRef<ImageViewerHandle, ImageViewerProps>(
    ({ image, alt = "Document image", resetImageState }, ref) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const imageRef = useRef<HTMLImageElement>(null);

        const [scale, setScale] = useState(1);
        const [rotation, setRotation] = useState(0);
        const [flip, setFlip] = useState(false);
        const [position, setPosition] = useState({ x: 0, y: 0 });
        const [dragging, setDragging] = useState(false);
        const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
        const [rotatedImageUrl, setRotatedImageUrl] = useState<string | null>(
            null,
        );

        // Rotate image using canvas
        useEffect(() => {
            if (!imageRef.current || rotation === 0) {
                setRotatedImageUrl(null);
                return;
            }

            const img = imageRef.current;
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            // For 90/270 degree rotations, swap width and height
            const isRotated90or270 = rotation === 90 || rotation === 270;
            canvas.width = isRotated90or270
                ? img.naturalHeight
                : img.naturalWidth;
            canvas.height = isRotated90or270
                ? img.naturalWidth
                : img.naturalHeight;

            // Translate and rotate
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate((rotation * Math.PI) / 180);
            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

            // Convert to data URL
            setRotatedImageUrl(canvas.toDataURL());
        }, [rotation, image]);

        // Reset all transformations
        const resetAll = () => {
            setScale(1);
            setRotation(0);
            setFlip(false);
            setPosition({ x: 0, y: 0 });
        };

        // Expose reset method via ref
        useImperativeHandle(ref, () => ({
            reset: resetAll,
        }));

        // Handle reset from parent
        useEffect(() => {
            if (resetImageState) {
                resetAll();
            }
        }, [resetImageState]);

        // Zoom controls with min/max limits
        const zoomIn = () => setScale((prev) => Math.min(prev + 0.25, 3));
        const zoomOut = () => setScale((prev) => Math.max(prev - 0.25, 0.5));

        // Rotation - simple rotation without auto-scaling
        const rotateLeft = () => {
            setRotation((prev) => (prev + 90) % 360);
        };

        // Mouse and touch handlers for panning
        const handleMouseDown = (e: React.MouseEvent) => {
            setDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y,
            });
        };

        const handleMouseMove = (e: React.MouseEvent) => {
            if (dragging) {
                setPosition({
                    x: e.clientX - dragStart.x,
                    y: e.clientY - dragStart.y,
                });
            }
        };

        const handleMouseUp = () => {
            setDragging(false);
        };

        // Touch handlers
        const handleTouchStart = (e: React.TouchEvent) => {
            const touch = e.touches[0];
            setDragging(true);
            setDragStart({
                x: touch.clientX - position.x,
                y: touch.clientY - position.y,
            });
        };

        const handleTouchMove = (e: React.TouchEvent) => {
            if (dragging && e.touches.length === 1) {
                const touch = e.touches[0];
                setPosition({
                    x: touch.clientX - dragStart.x,
                    y: touch.clientY - dragStart.y,
                });
                // Only prevent default if we're actually moving the image horizontally
                // This allows vertical scrolling when needed
                if (Math.abs(touch.clientX - dragStart.x) > 10) {
                    e.preventDefault();
                }
            }
        };

        const handleTouchEnd = () => {
            setDragging(false);
        };

        // We're not handling wheel events - natural scrolling is enabled via overflow-auto

        return (
            <div className="w-full h-full flex flex-col">
                {/* Control toolbar */}
                <div className="flex gap-1 p-2 justify-center">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={zoomIn}
                        disabled={scale >= 3}
                    >
                        <ZoomIn className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={zoomOut}
                        disabled={scale <= 0.5}
                    >
                        <ZoomOut className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={resetAll}
                        disabled={scale === 1 && rotation === 0 && !flip}
                    >
                        <RefreshCcw className="h-4 w-4" />
                    </Button>
                    <span className="text-sm flex items-center px-2 w-16 justify-center">
                        {Math.round(scale * 100)}%
                    </span>

                    <Button variant="outline" size="sm" onClick={rotateLeft}>
                        <RotateCwSquare className="h-4 w-4" />
                    </Button>
                </div>

                {/* Image viewer */}
                <div
                    ref={containerRef}
                    className="flex-1 overflow-auto"
                    onMouseDown={disableGestures ? undefined : handleMouseDown}
                    onMouseMove={disableGestures ? undefined : handleMouseMove}
                    onMouseUp={disableGestures ? undefined : handleMouseUp}
                    onMouseLeave={disableGestures ? undefined : handleMouseUp}
                    onTouchStart={
                        disableGestures ? undefined : handleTouchStart
                    }
                    onTouchMove={disableGestures ? undefined : handleTouchMove}
                    onTouchEnd={disableGestures ? undefined : handleTouchEnd}
                    onTouchCancel={disableGestures ? undefined : handleTouchEnd}
                    style={{ cursor: dragging ? "grabbing" : "grab" }}
                >
                    {/* Hidden original image for canvas rotation */}
                    <img
                        ref={imageRef}
                        src={image}
                        alt=""
                        style={{ display: "none" }}
                        crossOrigin="anonymous"
                    />
                    {/* Displayed image (original or rotated) */}
                    <img
                        className="w-full h-auto object-contain block"
                        style={{
                            transform: `scale(${scale}) scaleX(${flip ? -1 : 1})`,
                            transformOrigin: "top left",
                            transition: "transform 0.1s ease-out",
                        }}
                        src={rotatedImageUrl || image}
                        alt={alt}
                        draggable={false}
                    />
                </div>
            </div>
        );
    },
);

// Display name for debugging
ImageViewer.displayName = "ImageViewer";

export { ImageViewer };
